import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { audiencesQueryKeys } from '@/features/audiencias/api/query-keys';
import { broadcastScheduleQueryKeys } from '@/features/broadcast-schedule/api/query-keys';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { NestedNullable } from '@/types/types';
import { emptyStringValues } from '@/utils/objects';

export async function patchProgramBroadcast(
  body: NestedNullable<
    Partial<
      Omit<
        TProgramBroadcast,
        'program_id' | 'created_at' | 'created_by' | 'updated_at' | 'updated_by'
      >
    >
  >
): Promise<TProgramBroadcast> {
  return mediaManagerClient.patch(`/api/v1/program_broadcasts/${body.id}`, emptyStringValues(body));
}

type MutationFn = typeof patchProgramBroadcast;

type TUsePatchProgramParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchProgramBroadcast({ config }: TUsePatchProgramParams = {}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    onError(error) {
      addErrorNotification(t('No se ha podido actualizar el programa'));
      console.error(error);
    },
    onSuccess(data, variables, context) {
      config?.onSuccess?.(data, variables, context);
      addSuccessNotification(t('El programa se ha actualizado correctamente'));
      queryClient.invalidateQueries({
        queryKey: broadcastScheduleQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: audiencesQueryKeys.all,
      });
    },
    mutationFn: patchProgramBroadcast,
  });
}
